/**
 * Session Service
 * Handles all app session-related API calls
 */

import apiClient from '../client';
import { API_ENDPOINTS } from '../config';
import {
  AppSession,
  AppSessionsParams,
  PaginatedResponse,
  VersionStats,
} from '../types';

class SessionService {
  /**
   * Get all app sessions
   */
  async getAppSessions(params?: AppSessionsParams): Promise<PaginatedResponse<AppSession>> {
    const response = await apiClient.get<PaginatedResponse<AppSession>>(
      API_ENDPOINTS.SESSIONS.APP,
      { params }
    );
    return response.data;
  }

  /**
   * Get app session by ID
   */
  async getSessionById(id: string): Promise<AppSession> {
    const response = await apiClient.get<AppSession>(
      API_ENDPOINTS.SESSIONS.BY_ID(id)
    );
    return response.data;
  }

  /**
   * Get session statistics
   */
  async getSessionStats(params?: {
    dateFrom?: string;
    dateTo?: string;
    version?: string;
    platform?: string;
    country?: string;
  }): Promise<{
    totalSessions: number;
    activeSessions: number;
    averageDuration: number;
    uniqueUsers: number;
  }> {
    const response = await apiClient.get<{
      totalSessions: number;
      activeSessions: number;
      averageDuration: number;
      uniqueUsers: number;
    }>(API_ENDPOINTS.SESSIONS.STATS, { params });
    return response.data;
  }

  /**
   * Get opened sessions statistics
   */
  async getOpenedSessionsStats(): Promise<{
    stats: VersionStats[];
    totals: {
      last24h: number;
      lastWeek: number;
      total: number;
    };
  }> {
    const response = await apiClient.get<{
      stats: VersionStats[];
      totals: {
        last24h: number;
        lastWeek: number;
        total: number;
      };
    }>(API_ENDPOINTS.SESSIONS.OPENED);
    return response.data;
  }

  /**
   * Get session trends
   */
  async getSessionTrends(params?: {
    period?: 'hour' | 'day' | 'week' | 'month';
    version?: string;
    platform?: string;
  }): Promise<Array<{
    date: string;
    sessionCount: number;
    uniqueUsers: number;
    averageDuration: number;
  }>> {
    const response = await apiClient.get<Array<{
      date: string;
      sessionCount: number;
      uniqueUsers: number;
      averageDuration: number;
    }>>(`${API_ENDPOINTS.SESSIONS.STATS}/trends`, { params });
    return response.data;
  }

  /**
   * Get sessions by platform
   */
  async getSessionsByPlatform(params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Array<{
    platform: string;
    sessionCount: number;
    percentage: number;
  }>> {
    const response = await apiClient.get<Array<{
      platform: string;
      sessionCount: number;
      percentage: number;
    }>>(`${API_ENDPOINTS.SESSIONS.STATS}/by-platform`, { params });
    return response.data;
  }

  /**
   * Get sessions by country
   */
  async getSessionsByCountry(params?: {
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
  }): Promise<Array<{
    country: string;
    sessionCount: number;
    percentage: number;
  }>> {
    const response = await apiClient.get<Array<{
      country: string;
      sessionCount: number;
      percentage: number;
    }>>(`${API_ENDPOINTS.SESSIONS.STATS}/by-country`, { params });
    return response.data;
  }

  /**
   * Get sessions by version
   */
  async getSessionsByVersion(params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Array<{
    version: string;
    sessionCount: number;
    percentage: number;
  }>> {
    const response = await apiClient.get<Array<{
      version: string;
      sessionCount: number;
      percentage: number;
    }>>(`${API_ENDPOINTS.SESSIONS.STATS}/by-version`, { params });
    return response.data;
  }

  /**
   * Get chart data for sessions
   */
  async getSessionChartData(
    chartType: 'bar' | 'line',
    dataType: 'count' | 'duration' | 'users'
  ): Promise<Array<{ name: string; [key: string]: string | number }>> {
    const response = await apiClient.get<Array<{ name: string; [key: string]: string | number }>>(
      `${API_ENDPOINTS.SESSIONS.STATS}/chart`,
      {
        params: {
          chartType,
          dataType,
        },
      }
    );
    return response.data;
  }

  /**
   * Create app session
   */
  async createSession(data: Partial<AppSession>): Promise<AppSession> {
    const response = await apiClient.post<AppSession>(
      API_ENDPOINTS.SESSIONS.APP,
      data
    );
    return response.data;
  }

  /**
   * End app session
   */
  async endSession(id: string): Promise<AppSession> {
    const response = await apiClient.patch<AppSession>(
      `${API_ENDPOINTS.SESSIONS.BY_ID(id)}/end`,
      {}
    );
    return response.data;
  }

  /**
   * Export app sessions
   */
  async exportSessions(params?: AppSessionsParams): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(
      `${API_ENDPOINTS.SESSIONS.APP}/export`,
      {
        params,
        responseType: 'blob',
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const sessionService = new SessionService();
export default sessionService;

